/**
 * Settings search (M286 idea 12): ⌘K / Ctrl-K anywhere, or the Settings hub's search box, opens a palette over the
 * settings this person may open (useSettingsSearchIndex). Type, then Enter goes to the first result, or ↑/↓ pick
 * another; Escape closes. Mounted once, in the authenticated Layout.
 */
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { DialogShell, DialogTitlePrimitive, PICKER_BOX, PICKER_OVERLAY } from '@/components/ui/dialog';
import SearchInput from '@/components/ui/SearchInput';
import { HighlightedText } from '@/components/ui/HighlightedText';
import { useGlobalShortcut } from '@/hooks/useGlobalShortcut';
import { SETTINGS_SEARCH_KIND_LABELS, searchSettingsIndex, useSettingsSearchIndex } from './useSettingsSearchIndex';

const OPEN_EVENT = 'settings-search:open';
/** A group lists this many results; more means keep typing. */
const GROUP_LIMIT = 8;

/** Open the palette from anywhere (the hub's search box). */
export const openSettingsSearch = () => window.dispatchEvent(new Event(OPEN_EVENT));

export function SettingsSearch() {
  const navigate = useNavigate();
  const index = useSettingsSearchIndex();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  useGlobalShortcut('k', () => setOpen((o) => !o));
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);
  // Every opening starts fresh.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  const groups = useMemo(() => searchSettingsIndex(index, query).map((g) => ({ ...g, shown: g.items.slice(0, GROUP_LIMIT) })), [index, query]);
  const results = groups.flatMap((g) => g.shown);

  const go = (to: string) => {
    setOpen(false);
    void navigate(to);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (results.length) setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : results.length - 1)) % results.length);
    } else if (e.key === 'Enter' && results[active]) {
      e.preventDefault();
      go(results[active].to);
    }
  };

  return (
    <DialogShell open={open} onClose={() => setOpen(false)} overlayClassName={PICKER_OVERLAY} className={PICKER_BOX}>
      <div className="flex min-h-0 flex-col" onKeyDown={onKeyDown} data-testid="settings-search">
        <div className="border-b border-gray-200 p-3">
          <DialogTitlePrimitive className="sr-only">Search settings</DialogTitlePrimitive>
          <SearchInput
            mode="find"
            value={query}
            onChange={(v) => {
              setQuery(v);
              setActive(0);
            }}
            placeholder="Search settings, pages and reference data…"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2" role="listbox" aria-label="Settings search results">
          {results.length === 0 && <p className="px-3 py-6 text-center text-sm text-gray-500">Nothing you can open matches “{query.trim()}”.</p>}
          {groups.map((g) => (
            <div key={g.kind} className="mb-2">
              <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{SETTINGS_SEARCH_KIND_LABELS[g.kind]}</div>
              {g.shown.map((item) => {
                const i = results.indexOf(item);
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="option"
                    aria-selected={i === active}
                    onMouseMove={() => setActive(i)}
                    onClick={() => go(item.to)}
                    className={`block w-full rounded-md px-3 py-2 text-left ${i === active ? 'bg-gray-100' : ''}`}
                  >
                    <div className="text-sm font-medium text-gray-900">
                      <HighlightedText text={item.title} searchTerm={query} />
                    </div>
                    <div className="truncate text-xs text-gray-500">
                      <HighlightedText text={item.crumb} searchTerm={query} />
                      {item.description && <> · {item.description}</>}
                    </div>
                  </button>
                );
              })}
              {g.items.length > g.shown.length && <div className="px-3 py-1 text-xs text-gray-400">{g.items.length - g.shown.length} more: keep typing to narrow</div>}
            </div>
          ))}
        </div>
      </div>
    </DialogShell>
  );
}

/** The Settings hub's search box: opens the palette. */
export function SettingsSearchBox() {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <button
      type="button"
      onClick={openSettingsSearch}
      data-testid="settings-search-box"
      className="flex w-full max-w-md items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-500 hover:border-gray-400"
    >
      <Search className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
      <span className="flex-1">Search settings</span>
      <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 text-xs text-gray-500">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
    </button>
  );
}
